import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Sparkles, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'gg_referral_code';

/**
 * Public landing page hit by anyone who clicks a personal referral link:
 *   gleamgurus.com/r/JOHN-L1HIA
 *
 * On mount:
 *  - Validates the code against the API
 *  - If valid, stashes it in localStorage so it travels through to /register
 *  - Renders a welcome banner ("John referred you — get 10% off your first clean")
 *
 * The signup form (pages/auth/AuthPage) reads localStorage[gg_referral_code]
 * and submits it inside POST /api/auth/register, which auto-flags the new
 * client as having a pending 10%-off welcome discount.
 */
export default function ReferralLandingPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/referrals/code/${code}`);
        if (cancelled) return;
        if (r.data?.valid) {
          localStorage.setItem(STORAGE_KEY, r.data.code);
        }
        setInfo(r.data);
      } catch (_e) {
        setInfo({ valid: false, code });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  if (!info?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-2xl font-bold text-white">That referral link isn't valid</h1>
          <p className="text-zinc-400">The code <code className="text-[#F56F05]">{code}</code> couldn't be found. No worries — you can still book.</p>
          <Button asChild className="bg-[#F56F05] hover:bg-[#e06505] text-white" data-testid="continue-without-code">
            <Link to="/">Browse Gleam Guru</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6" data-testid="referral-landing">
      <div className="max-w-xl w-full space-y-8 text-center">
        <div className="inline-flex items-center gap-2 bg-[#F56F05]/10 border border-[#F56F05]/30 px-4 py-2 rounded-full">
          <Sparkles className="w-4 h-4 text-[#F56F05]" />
          <span className="text-sm font-semibold text-[#F56F05] uppercase tracking-wider">Referred by {info.referrer_first_name}</span>
        </div>

        <div className="space-y-3">
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
            {info.referrer_first_name} just gifted you<br/>
            <span className="text-[#F56F05]">{info.discount_percent}% off your first clean</span>
          </h1>
          <p className="text-zinc-400 text-lg">
            Premium home cleaning across Portland metro. Insured, vetted, and rated.
          </p>
        </div>

        <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 text-left space-y-2.5">
          {[
            'Your 10% off applies automatically when we send your first invoice',
            'No promo code to remember — we have you covered',
            'Plus: after 2 cleanings, you earn 10% off too when you refer a friend',
          ].map(t => (
            <div key={t} className="flex items-start gap-2.5 text-zinc-300 text-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <span>{t}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={() => navigate('/booking')}
            className="bg-[#F56F05] hover:bg-[#e06505] text-white px-8 py-6 text-base"
            data-testid="referral-cta-book"
          >
            Get my 10% off — book a clean <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/services')}
            className="border-zinc-700 text-zinc-200 hover:bg-zinc-800 px-8 py-6 text-base"
            data-testid="referral-cta-services"
          >
            See services first
          </Button>
        </div>

        <p className="text-xs text-zinc-500">Code <code className="text-zinc-300">{info.code}</code> applied automatically.</p>
      </div>
    </div>
  );
}
